// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable jsx-a11y/alt-text */
import { Button as MUIButton, CircularProgress } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { ExplorerUrl, SlippageToolTip } from '@mogul/shared';
import { fetchFeeData } from '@wagmi/core';
// import axios from 'axios';
import { BigNumber, ethers } from 'ethers';
import { debounce } from 'lodash';
import type { FormEventHandler } from 'react';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import toast, { Toaster } from 'react-hot-toast';
import * as r from 'runtypes';

import MetaMaskFoxIcon from '../../common/images/metamask/metamask-fox.png';
import { ReactComponent as RedoIcon } from '../../common/svg/button-icons/redo-arrow-icon.svg';
import { ReactComponent as TetherIcon } from '../../common/svg/button-icons/tether-logo.svg';
import { ReactComponent as CheckIcon } from '../../common/svg/general/green-check.svg';
import { ReactComponent as XIcon } from '../../common/svg/general/icon-x.svg';
import { ReactComponent as MogulIcon } from '../../common/svg/general/logo-mogul.svg';
import { CoinGeckoContext } from '../../contexts/app-state-context/coingecko/coingecko.context';
import { TradeStateContext } from '../../contexts/app-state-context/trade/trade.context';
import { useSigner, useWeb3State } from '../../hooks';
import { WEB3_CONFIG } from '../../lib';
import {
  addStarsTokenToWallet,
  formatEther,
  parseEther,
  weiToEth,
} from '../../lib/smart-contract-utils';
import { getPancakeTradeMinimumAmountOut } from '../../lib/uniswap/uniswap';
import { Button } from '../core/button/Button';
import { InputField } from '../core/input-field';
import { Tooltip } from '../core/tooltip';
import styles from './BuyStarsModal.module.css';

const useStyles = makeStyles(() =>
  createStyles({
    loader: {
      color: 'white',
    },
  }),
);

const parseFloatField = (value: string, prevValue: string) => {
  const regex = /^\d*[.]?\d*$/g;
  const floatString = value.match(regex)
    ? value.match(regex)?.toString()
    : prevValue;
  return floatString;
};

const formatFloatField = (value?: string) => {
  return value === undefined ? '' : value;
};

export interface BuyStarsFormValues {
  usdt?: string;
}

enum ModalState {
  buy = 'BUY',
  transaction = 'TRANSACTION',
}

// eslint-disable-next-line @typescript-eslint/unbound-method
const validate = r.Record({ usdt: r.String }).guard;

type SelectionRef = {
  start: number | null;
  end: number | null;
  element: HTMLInputElement;
} | null;

export interface BuyStarsModalProps {
  onClose: () => void;
}

export const BuyStarsModal = ({ onClose }: BuyStarsModalProps) => {
  const classes = useStyles();
  const { walletDetails } = useWeb3State();
  const { starsUsdRate, handleGetRates } = useContext(CoinGeckoContext);
  const { trade, value, updateTrade, executeTrade, initialized } =
    useContext(TradeStateContext);

  const [slippageToleranceRaw, setSlippageToleranceRaw] = useState(0.5); // Text shown percentage
  // const [slippageTolerance, setSlippageTolerance] = useState(
  //   new Percent('50', '10000'), // 50 bips, or 0.50%
  // );
  const [slippageTolerance, setSlippageTolerance] = useState('50');
  const [gasPrice, setGasPrice] = useState('');

  const [outputAmount, setOutputAmount] = useState<string | undefined>();
  const [values, setValues] = useState<BuyStarsFormValues>({
    usdt: formatEther(BigNumber.from(value)).toString(),
  });

  const [submitting, setSubmitting] = useState(false);
  const [modalState, setModalState] = useState(ModalState.buy);
  const [transactionUrl, setTransactionUrl] = useState('');
  const [starsField, setStarsField] = useState<HTMLInputElement | null>(null);
  const [updateStars, setUpdateStars] = useState('');
  const [setUp, setSetUp] = useState(false);
  const selection = useRef<SelectionRef>(null);
  const signer = useSigner();

  const valid = validate(values);

  const input = useCallback(
    <T extends keyof BuyStarsFormValues>(
      field: T,
      parse: (value: string, prevValue: string) => BuyStarsFormValues[T],
      format: (value: BuyStarsFormValues[T]) => string,
    ) => ({
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        const target = event.currentTarget;

        const inputValue = target.value;
        const parsed = parse(inputValue, values[field] ?? '');

        selection.current = {
          element: target,
          start: target.selectionStart,
          end: target.selectionEnd,
        };

        let isValid = false;
        setValues(v => {
          const newState = { ...v, [field]: parsed };

          isValid = validate(newState);

          return newState;
        });

        if (!isValid || parseFloat(inputValue) <= 0) {
          return;
        }

        updateTrade(parseEther(inputValue).toString());
      },
      value: format(values[field]),
    }),
    [values, updateTrade],
  );

  // useEffect(() => {
  //   if (
  //     walletInfo.currentNetwork === 'RINKEBY' ||
  //     walletInfo.currentNetwork === 'GOERLI' ||
  //     walletInfo.currentNetwork === 'BSCTEST'
  //   ) {
  //     // todo: ETH?
  //     // toast.error(
  //     //   'NOTE: Modal on test networks will display live MOGUL-ETH conversion rates',
  //     // );
  //   }
  // }, [walletInfo.currentNetwork]);

  const handleClose = useCallback(() => {
    if (submitting) {
      return;
    }

    onClose();
  }, [onClose, submitting]);

  // custom slippage input handler
  const setSlippage = useCallback((percentage: number) => {
    if (!percentage) {
      setSlippageToleranceRaw(percentage);

      return;
    }

    // slippage max of 50% following uniswap
    const slippageValue = Math.max(
      Number(0),
      Math.min(Number(50), Number(percentage)),
    );
    // plus epsilon to prevent incorrect rounding
    const conversion = Math.round(
      (slippageValue + Number.EPSILON) * 100,
    ).toString();
    // setSlippageTolerance(new Percent(conversion, '10000'));
    setSlippageTolerance(conversion);
    setSlippageToleranceRaw(slippageValue);
  }, []);

  const addStarsToWallet = useCallback(
    async () => addStarsTokenToWallet(walletDetails.chain.key),
    [walletDetails.chain.key],
  );

  const getCurrentGasPrice = useCallback(async () => {
    const fees = await fetchFeeData({ formatUnits: 'ether' });

    setGasPrice(fees.formatted.gasPrice ?? '');
  }, []);

  const setMax = useCallback(async () => {
    // const adjusted = (
    //   parseFloat(walletInfo.ethBalance) - parseFloat(gasPrice)
    // ).toString();
    // const parsed = parseFloatField(adjusted, '');
    // await getCurrentGasPrice();
    // setValues({ eth: adjusted });
    // updateTrade(parseEther(parsed ?? '').toString());

    updateTrade(walletDetails.balance.usdt.raw.toString());
    setValues({ usdt: walletDetails.balance.usdt.formatted });

    const minAmountOut = await getPancakeTradeMinimumAmountOut(trade);
    const starsAmount = minAmountOut.toFixed(0);

    setOutputAmount(weiToEth(BigNumber.from(starsAmount)));
  }, [walletDetails.balance.usdt.raw, walletDetails.balance.usdt.formatted]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = async event => {
    event.preventDefault();

    if (!signer || !walletDetails.address || submitting || !validate(values)) {
      return;
    }

    setSubmitting(true);

    try {
      const txn = await executeTrade(
        signer,
        walletDetails.address,
        slippageTolerance,
      );
      /** @ts-expect-error - type issue  */
      const url = `${walletDetails.chain.explorerUrl}/tx/${txn.transactionHash}`;

      setTransactionUrl(url);
      setModalState(ModalState.transaction);
      toast.success(`MOGUL added!`);
    } catch (error) {
      setSubmitting(false);
      throw error;
    }

    setSubmitting(false);
  };

  useEffect(() => void getCurrentGasPrice(), []);

  useEffect(() => {
    console.log('Current Network', { chain: walletDetails.chain });
    const isBsc = walletDetails.chain.id === WEB3_CONFIG.chains.bsc.id;

    if (!isBsc) {
      toast.error('Please switch to Binance Smart Chain');
      onClose();
    } else {
      setSetUp(true);
    }
  }, [walletDetails.chain.id]);

  useEffect(() => {
    const isCurrentActive =
      selection.current?.element === document.activeElement;

    if (!isCurrentActive) {
      return;
    }

    selection.current!.element.selectionStart = selection.current!.start;
    selection.current!.element.selectionEnd = selection.current!.end;
  });

  // update stars amount based on trade context changes
  // let starsAmount = trade.minimumAmountOut(slippageTolerance).raw.toString();
  useEffect(() => {
    const updateOutputAmount = async () => {
      const minAmountOut = await getPancakeTradeMinimumAmountOut(trade);
      const starsAmount = minAmountOut.toFixed(0);

      setOutputAmount(weiToEth(BigNumber.from(starsAmount)));
    };

    const debouncedUpdateOutputAmount = debounce(updateOutputAmount, 300);

    // starsAmount = trade.minimumAmountOut(slippageTolerance).raw.toString();

    void debouncedUpdateOutputAmount();

    return () => debouncedUpdateOutputAmount.cancel();
  }, [
    // starsAmount,
    initialized,
    updateStars,
    values,
    value,
    trade,
    starsField,
    slippageTolerance,
  ]);

  // price ratios

  return (
    <>
      {setUp ? (
        <Toaster
          toastOptions={{
            success: {
              duration: 5000,
            },
          }}
        />
      ) : null}
      {modalState === ModalState.buy && (
        <div
          className={`${styles.modalBG} max-w-screen-sm lg:max-w-full relative`}
        >
          {submitting && (
            <div className={styles.starsLoading}>
              <CircularProgress
                className={`${classes.loader} mb-5`}
                size={85}
              />
              <div className={styles.description}>Awaiting Transaction</div>
            </div>
          )}
          <form autoComplete="off" onSubmit={handleSubmit}>
            <div
              className={styles.xIconBG}
              onClick={handleClose}
              onKeyPress={undefined}
              role="button"
              tabIndex={0}
            >
              <XIcon className={styles.xIcon} />
            </div>
            <div>
              <div className="flex flex-row justify-center mb-4">
                <div className="flex flex-col flex-grow justify-center relative">
                  <div className={`${styles.title} fontVanguard font-bold `}>
                    BUY MOGUL
                  </div>
                  <hr className={styles.titleHR} />
                </div>
                {/*<div className="flex flex-row relative justify-end mt-2 ml-20">*/}
                {/*  {nativeToken === 'ETH' ? (*/}
                {/*    <>*/}
                {/*      <a*/}
                {/*        href="https://uniswap.org/"*/}
                {/*        target="_blank"*/}
                {/*        rel="noopener noreferrer"*/}
                {/*        style={{ textDecoration: 'underline' }}*/}
                {/*      >*/}
                {/*        <UniSwapIcon fill="black" />*/}
                {/*      </a>*/}
                {/*    </>*/}
                {/*  ) : (*/}
                {/*    <a*/}
                {/*      href="https://apeswap.finance"*/}
                {/*      target="_blank"*/}
                {/*      rel="noopener noreferrer"*/}
                {/*      style={{ textDecoration: 'underline' }}*/}
                {/*    >*/}
                {/*      <ApeSwapIcon fill="black" />*/}
                {/*    </a>*/}
                {/*  )}{' '}*/}
                {/*</div>*/}
              </div>

              <div className="text-left">
                <div>
                  <div className="pb-4 font-bold flex flex-row content-center items-center">
                    Slippage Tolerance{' '}
                    <Tooltip title={SlippageToolTip} placement="right-end">
                      <HelpOutlineIcon
                        className="ml-1 mr-2 opacity-50 place-self-center"
                        fontSize="small"
                      />
                    </Tooltip>
                  </div>
                  <div className="flex flex-col sm:flex-row">
                    <div>
                      <MUIButton
                        style={
                          slippageToleranceRaw === 0.1
                            ? {
                                backgroundColor: 'rgba(247, 247, 247, 1)',
                                color: 'black',
                                borderRadius: '100px',
                                fontWeight: 'bold',
                              }
                            : {
                                backgroundColor: 'rgba(126, 140, 180, 0.5)',
                                color: 'white',
                                borderRadius: '100px',
                                fontWeight: 'bold',
                              }
                        }
                        onClick={() => setSlippage(0.1)}
                      >
                        0.1%
                      </MUIButton>
                      <MUIButton
                        style={
                          slippageToleranceRaw === 0.5
                            ? {
                                backgroundColor: 'rgba(247, 247, 247, 1)',
                                color: 'black',
                                borderRadius: '100px',
                                marginLeft: '5px',
                                fontWeight: 'bold',
                              }
                            : {
                                backgroundColor: 'rgba(126, 140, 180, 0.5)',
                                color: 'white',
                                borderRadius: '100px',
                                marginLeft: '5px',
                                fontWeight: 'bold',
                              }
                        }
                        onClick={() => setSlippage(0.5)}
                      >
                        0.5%
                      </MUIButton>
                      <MUIButton
                        style={
                          slippageToleranceRaw === 1.0 ||
                          slippageToleranceRaw === 1
                            ? {
                                backgroundColor: 'rgba(247, 247, 247, 1)',
                                color: 'black',
                                borderRadius: '100px',
                                marginLeft: '5px',
                                fontWeight: 'bold',
                              }
                            : {
                                backgroundColor: 'rgba(126, 140, 180, 0.5)',
                                color: 'white',
                                borderRadius: '100px',
                                marginLeft: '5px',
                                fontWeight: 'bold',
                              }
                        }
                        onClick={() => setSlippage(1.0)}
                      >
                        1.0%
                      </MUIButton>
                    </div>
                    <div
                      className={`${styles.inputSlippageContainer} w-11/12 sm:w-2/12 mr-auto sm:ml-2 flex flex-row mt-2 sm:mt-0`}
                    >
                      <input
                        type="number"
                        className={`${styles.inputSlippageField} w-full font-bold`}
                        onChange={e => setSlippage(e.target.valueAsNumber)}
                        value={slippageToleranceRaw}
                      />
                      <div className="place-self-center font-bold">%</div>
                    </div>
                  </div>
                </div>
                <div className="pt-5">
                  <div
                    className={`${styles.title} fontVanguard font-bold pb-4`}
                  >
                    USE YOUR USDT
                  </div>
                  <div className={`${styles.inputOuter}`}>
                    <div
                      className={`${styles.inputInner} flex flex-row justify-between`}
                    >
                      <div className="flex flex-row content-center items-center">
                        <div>
                          {/*{nativeToken === 'ETH' ? (*/}
                          {/*  <EthIcon className={styles.starsIcon} />*/}
                          {/*) : (*/}
                          {/*  <BscIcon className={styles.starsIcon} />*/}
                          {/*)}*/}
                          <TetherIcon className={styles.starsIcon} />
                        </div>
                        {/*<div*/}
                        {/*  className={`${styles.tokenName} place-self-center font-bold`}*/}
                        {/*>*/}
                        {/*  {nativeToken}*/}
                        {/*</div>*/}
                        <MUIButton
                          className={styles.maxButton}
                          style={{
                            backgroundColor: '#F4D257',
                            color: 'black',
                            borderRadius: '12px',
                            marginLeft: '15px',
                            fontWeight: 'bold',
                          }}
                          onClick={() => setMax()}
                          disabled={
                            parseFloat(walletDetails.balance.usdt.formatted) <=
                            0
                          }
                        >
                          MAX
                        </MUIButton>
                      </div>
                      <div
                        className={`${styles.inputFieldContainer} flex flex-row content-center place-self-center`}
                      >
                        <InputField
                          label=""
                          InputProps={{
                            id: 'nativeInput',
                            disabled: submitting,
                            ...input('usdt', parseFloatField, formatFloatField),
                            className: styles.inputFieldsOld,
                          }}
                        />
                      </div>
                    </div>
                    <div
                      className={`${styles.inputOuterText} flex flex-col lg:flex-row justify-between pt-3`}
                    >
                      <div className="flex flex-row">
                        Balance:
                        <div className="ml-3">
                          {walletDetails.balance.usdt.formatted} USDT
                        </div>
                      </div>
                      {/*<div>*/}
                      {/*  ~{' '}*/}
                      {/*  {(*/}
                      {/*    parseFloat(ethers.utils.formatEther(ethValue)) **/}
                      {/*    (nativeToken === 'ETH' ? ethUsdRate : bnbUsdRate)*/}
                      {/*  ).toLocaleString('en-US', {*/}
                      {/*    style: 'currency',*/}
                      {/*    currency: 'USD',*/}
                      {/*    minimumFractionDigits: 2,*/}
                      {/*    maximumFractionDigits: 2,*/}
                      {/*  }) + ' USD'}*/}
                      {/*</div>*/}
                    </div>
                  </div>
                </div>
                <div className="pt-5">
                  <div className="flex flex-row justify-between">
                    <div
                      className={`${styles.title} fontVanguard font-bold pb-4`}
                    >
                      BUY MOGUL
                    </div>

                    <Tooltip
                      title="Click to add MOGUL token to wallet"
                      placement="top"
                      arrow
                    >
                      <button
                        style={{ width: '50px' }}
                        onClick={(e: any) => {
                          e.preventDefault();
                          void addStarsToWallet();
                        }}
                        className="pb-3"
                      >
                        <img
                          src={MetaMaskFoxIcon}
                          style={{ width: '100%' }}
                          alt=""
                        />
                      </button>
                    </Tooltip>
                  </div>
                  <div className={`${styles.inputOuter}`}>
                    <div
                      className={`${styles.inputInner} flex flex-row justify-between`}
                    >
                      <div className="flex flex-row content-center items-center">
                        <div>
                          <MogulIcon className={styles.starsIcon} />
                        </div>
                        <div
                          className={`${styles.tokenName} place-self-center font-bold`}
                        >
                          MOGUL
                        </div>
                      </div>
                      <div
                        className={`${styles.inputFieldContainer} flex flex-row content-center place-self-center`}
                      >
                        <InputField
                          label=""
                          InputProps={{
                            ref: starsFieldValue => {
                              setUpdateStars(starsFieldValue?.value ?? '');
                              setStarsField(starsFieldValue);
                            },
                            readOnly: true,
                            value: outputAmount ?? '',
                            className: styles.inputFieldsOld,
                          }}
                        />
                      </div>
                    </div>
                    <div
                      className={`${styles.inputOuterText} flex flex-col lg:flex-row justify-between pt-3`}
                    >
                      <div className="flex flex-row">
                        Balance:
                        <div className="ml-3">
                          {walletDetails.balance.stars.formatted} MOGUL
                        </div>
                      </div>
                      {/*<div>*/}
                      {/*  ~{' '}*/}
                      {/*  {(*/}
                      {/*    parseFloat(outputAmount ?? '') * starsUsdRate*/}
                      {/*  ).toLocaleString('en-US', {*/}
                      {/*    style: 'currency',*/}
                      {/*    currency: 'USD',*/}
                      {/*    minimumFractionDigits: 2,*/}
                      {/*    maximumFractionDigits: 2,*/}
                      {/*  }) + ' USD'}*/}
                      {/*</div>*/}
                    </div>
                  </div>
                </div>
                <div className="flex flex-row justify-between pt-2">
                  <div>Price:</div>
                  <div>
                    {starsUsdRate.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 12,
                    })}
                    {/*{nativeToken === 'BNB' &&*/}
                    {/*  bnbStarsRate.toLocaleString('en-US', {*/}
                    {/*    minimumFractionDigits: 2,*/}
                    {/*    maximumFractionDigits: 12,*/}
                    {/*  })}*/}
                    {/*{nativeToken === 'ETH' &&*/}
                    {/*  ethStarsRate.toLocaleString('en-US', {*/}
                    {/*    minimumFractionDigits: 2,*/}
                    {/*    maximumFractionDigits: 12,*/}
                    {/*  })}{' '}*/}
                    USDT per MOGUL
                    <MUIButton
                      // onClick={getRatios}
                      onClick={handleGetRates}
                      style={{
                        padding: '0',
                        margin: '0',
                        minWidth: '0',
                        marginLeft: '15px',
                      }}
                    >
                      <RedoIcon />
                    </MUIButton>
                  </div>
                </div>
                <div className="flex flex-col w-full justify-center text-center pt-14">
                  <Button
                    type="submit"
                    fullWidth
                    disabled={
                      parseFloat(walletDetails.balance.usdt.formatted) <
                        parseFloat(values.usdt ?? '0') ||
                      !valid ||
                      parseFloat(walletDetails.balance.usdt.formatted) <= 0 ||
                      parseFloat(values.usdt ?? '0') <= 0
                    }
                  >
                    BUY
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}
      {modalState === ModalState.transaction && (
        <div className={`${styles.modalBG2} max-w-screen-sm lg:max-w-full`}>
          <div
            className={styles.xIconBG}
            onClick={handleClose}
            onKeyPress={undefined}
            role="button"
            tabIndex={0}
          >
            <XIcon className={styles.xIcon} />
          </div>
          <div>
            <div
              className={`${styles.title} fontVanguard font-bold text-center`}
            >
              PURCHASE COMPLETED
            </div>
            <hr className={styles.titleHR} />
            <div className="text-left">
              <div className="flex flex-col w-full justify-center text-center pt-8">
                <div>
                  <div className="mb-10">
                    <CheckIcon className={styles.greenCheck} />
                  </div>
                  <div className={`${styles.transactionTitle} mb-5`}>
                    Transaction Submitted
                  </div>
                  <div className="mb-20">
                    <a
                      href={transactionUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'underline' }}
                    >
                      View Transaction on Explorer
                    </a>
                  </div>
                </div>
                <Button
                  fullWidth
                  onClick={addStarsToWallet}
                  kind="secondary"
                  className="mb-5"
                >
                  <img
                    src={MetaMaskFoxIcon}
                    className={styles.metaMaskFoxIcon}
                    draggable={false}
                  />
                  Add Mogul to Metamask
                </Button>
                <Button fullWidth onClick={handleClose}>
                  Done
                </Button>
                {/*<div className="pt-4">*/}
                {/*  Powered by{' '}*/}
                {/*  {nativeToken === 'ETH' ? (*/}
                {/*    <a*/}
                {/*      href="https://uniswap.org/"*/}
                {/*      target="_blank"*/}
                {/*      rel="noopener noreferrer"*/}
                {/*      style={{ textDecoration: 'underline' }}*/}
                {/*    >*/}
                {/*      UniSwap*/}
                {/*    </a>*/}
                {/*  ) : (*/}
                {/*    <a*/}
                {/*      href="https://apeswap.finance"*/}
                {/*      target="_blank"*/}
                {/*      rel="noopener noreferrer"*/}
                {/*      style={{ textDecoration: 'underline' }}*/}
                {/*    >*/}
                {/*      ApeSwap*/}
                {/*    </a>*/}
                {/*  )}*/}
                {/*</div>*/}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const createBuyStarsModal =
  () =>
  ({ hideModal }: { hideModal: () => void }) => {
    return <BuyStarsModal onClose={() => hideModal()} />;
  };
